import PropTypes from 'prop-types';

import { CloseIcon, ErrorText, IconButton, RichTextInput } from '@utilities';

import { getTranslationModalMessage } from '../../utilities/getTranslationModalMessage';
import { getTranslationWarnings } from '../../utilities/translationValidation';

import styles from './_index.module.scss';

const WarningLine = ({ children, message }) => (
  <ErrorText className={styles['modal-translation-row-warning']}>
    <span>{message}</span>
    {children}
  </ErrorText>
);

WarningLine.propTypes = {
  children: PropTypes.node,
  message: PropTypes.string.isRequired,
};

const TranslationRow = ({
  appliedLanguages,
  entityKey,
  fallbackToSource,
  getEntry,
  getTranslation,
  hasConceptName,
  isDisabled,
  languageLabels,
  onClear,
  onDismissRelevanceWarning,
  onPrioritizeTranslation,
  onTranslate,
  onTranslationChange,
  pendingTranslationKeys,
  sourceIcon,
  sourceLanguage,
  sourceRichText,
  text,
}) => (
  <div className={styles['modal-translation-row']}>
    <span className={styles['modal-translation-row-source']}>
      {sourceIcon && (
        <span className={styles['modal-translation-row-source-icon']}>{sourceIcon}</span>
      )}
      <span>{text}</span>
    </span>
    {appliedLanguages.length > 0 && (
      <>
        <div className={styles['modal-translation-row-input']}>
          {appliedLanguages.map((language) => {
            const entry = getEntry?.(entityKey, language);
            const showRelevanceWarning = Boolean(
              entry && entry.isRelevant === false && entry.isUserUpdated === true
            );
            const translationRichText = getTranslation(entityKey, language);
            // Derived warnings clear automatically as the translation is fixed.
            const validationWarnings = getTranslationWarnings({
              language: sourceLanguage,
              sourceRichText: sourceRichText || text,
              translationRichText,
            });
            const hasMessages = validationWarnings.length > 0 || showRelevanceWarning;
            const isTranslationPending = pendingTranslationKeys?.has(`${entityKey}:${language}`);
            return (
              <div className={styles['modal-translation-row-field']} key={language}>
                <div className={styles['modal-translation-row-textbox']}>
                  <RichTextInput
                    hasConceptName={hasConceptName}
                    isDisabled={isDisabled}
                    onChange={(next) => onTranslationChange(entityKey, language, next)}
                    onFocus={onPrioritizeTranslation}
                    onTranslate={onTranslate ? () => onTranslate(language) : undefined}
                    placeholder={languageLabels[language]}
                    value={translationRichText || (fallbackToSource ? text : '')}
                  />
                  {isTranslationPending && (
                    <span aria-live="polite" className={styles['modal-translation-row-loading']}>
                      Translating...
                    </span>
                  )}
                </div>
                {hasMessages && (
                  <div className={styles['modal-translation-row-messages']}>
                    {validationWarnings.map((warning) => (
                      <WarningLine key={warning} message={warning} />
                    ))}
                    {showRelevanceWarning && (
                      <WarningLine
                        message={getTranslationModalMessage('relevanceWarning', sourceLanguage)}
                      >
                        <button
                          className={styles['modal-translation-row-warning-dismiss']}
                          disabled={isDisabled}
                          onClick={() => onDismissRelevanceWarning(entityKey, language)}
                          type="button"
                        >
                          Dismiss
                        </button>
                      </WarningLine>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {onClear && (
          <IconButton
            aria-label={`Clear translation for ${text}`}
            className={styles['modal-translation-row-clear']}
            isDisabled={isDisabled}
            onClick={onClear}
            tooltip={`Clear translation for ${text}`}
            type="button"
          >
            <CloseIcon />
          </IconButton>
        )}
      </>
    )}
  </div>
);

TranslationRow.propTypes = {
  appliedLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
  entityKey: PropTypes.string.isRequired,
  fallbackToSource: PropTypes.bool,
  getEntry: PropTypes.func,
  getTranslation: PropTypes.func.isRequired,
  hasConceptName: PropTypes.bool,
  isDisabled: PropTypes.bool,
  languageLabels: PropTypes.object.isRequired,
  onClear: PropTypes.func,
  onDismissRelevanceWarning: PropTypes.func,
  onPrioritizeTranslation: PropTypes.func,
  onTranslate: PropTypes.func,
  onTranslationChange: PropTypes.func.isRequired,
  pendingTranslationKeys: PropTypes.instanceOf(Set),
  sourceIcon: PropTypes.node,
  sourceLanguage: PropTypes.string,
  sourceRichText: PropTypes.string,
  text: PropTypes.string,
};

export default TranslationRow;
